/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { type ProductShelfYearsData, type YearsResponse } from '../interface';
import { type GetYearsBlockUsingGetParamsModel } from '@/api/types/browse-search-types';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useRouter } from 'next/router';
import { parseUrl } from '@/utils/urlHelpers';
import { type QueryClient } from '@tanstack/react-query';
import type {
  GetShopByYearParamsModel,
  ShopByYearsResponseModel,
} from '@/api/types/product-navigation-search-types';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useFeatureFlag } from '@/features/globalConfig';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-seo-data-bs/v2/category-years`;

const ACES_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-navigation-search/v1/shop-by-page-year-list`;

type Options = {
  seoUrl: string;
  locale: Locale;
  year: string | number | null | undefined;
  preview: boolean;
  shouldCallACES: boolean;
  acesOptions: {
    makeId: number | null | undefined;
    modelId: number | null | undefined;
    taxonomyPath?: string;
    categoryIdPath?: string;
    partGroupId?: string;
  };
};

type OptionsQuery = Omit<Options, 'locale' | 'preview' | 'shouldCallACES' | 'seoUrl'> & {
  seoUrl?: string;
  enabled?: boolean;
};

const getCategoryYears = async (
  { seoUrl, locale, year, preview, shouldCallACES, acesOptions }: Options,
  axiosInstance?: AxiosInstance
) => {
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';
  const yearBlockType = year ? 'ALTERNATE' : 'ALL';

  if (shouldCallACES) {
    const { makeId, modelId, taxonomyPath, categoryIdPath, partGroupId } = acesOptions;
    const params: GetShopByYearParamsModel = {
      country,
      customerType,
      salesChannel,
      preview,
      taxonomyPath,
      categoryIdPath,
      partGroupId,
      makeId: makeId!,
      modelId: modelId!,
      year: (year as number | undefined) || undefined,
      yearBlockType,
    };

    const response = await getAxios(axiosInstance).get<ShopByYearsResponseModel>(ACES_URL, {
      params,
      withCredentials: false,
    });

    return acesSelector(response.data);
  }

  const params: GetYearsBlockUsingGetParamsModel = {
    country,
    customerType,
    salesChannel,
    preview,
    seoUrl: parseUrl(seoUrl).url,
    yearBlockType,
  };
  const response = await getAxios(axiosInstance).get<YearsResponse>(URL, {
    params,
    withCredentials: false,
  });
  return selector(response.data);
};

const selector = (data: YearsResponse): ProductShelfYearsData => {
  const response = data.years.map((item) => ({
    name: item.linkDisplayText,
    seoUrl: item.seoUrl,
    makeModelYearPath: item.makeModelYearPath,
  }));

  return {
    shopByMMYList: response,
    title: data.title,
    canonicalPath: data.canonicalPath,
    taxonomyPath: data.taxonomyPath,
    pageType: data.pageType,
  };
};

const acesSelector = (data: ShopByYearsResponseModel): ProductShelfYearsData => {
  const { title, canonicalPath, taxonomyPath, pageType, years } = data;

  return {
    title,
    canonicalPath,
    taxonomyPath,
    pageType,
    shopByMMYList: (years ?? []).map(({ linkDisplayText, makeModelYearPath, seoUrl }) => ({
      makeModelYearPath,
      name: linkDisplayText,
      seoUrl,
    })),
  };
};

const { useData: useCategoryYearsData, prefetch: prefetchCategoryYearsData } = createQuery<
  ProductShelfYearsData,
  Options
>('categoryYears', getCategoryYears);

export const useCategoryYears = ({ enabled = true, ...rest }: OptionsQuery) => {
  const router = useRouter();
  const locale = useLocale();
  const shopByPageAPIsEnabled = useFeatureFlag('SHOP_BY_PAGE_APIS_ENABLED') === 'true';
  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();

  const shouldCallACES = acesEnabled && acesNonVehicleEndpointsEnabled && shopByPageAPIsEnabled;

  const acesExtraConditions = shouldCallACES
    ? Boolean(
        rest?.acesOptions?.makeId &&
          rest.acesOptions.modelId &&
          (rest.acesOptions.taxonomyPath ||
            rest.acesOptions.categoryIdPath ||
            rest.acesOptions.partGroupId)
      )
    : true;

  const options: Options = {
    seoUrl: router.asPath,
    ...rest,
    locale,
    shouldCallACES,
    preview: showXMPreviewDate(),
  };
  return useCategoryYearsData({
    enabled: enabled && acesExtraConditions,
    ...options,
  });
};

export const prefetchCategoryYears = (
  options: Options,
  queryClient: QueryClient,
  axios: AxiosInstance
) => {
  return prefetchCategoryYearsData(queryClient, options, axios);
};
